import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: Si on revient du callback OAuth, skip le check initial
    // AuthCallback va gérer l'authentification dans ce cas
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    // Dispatch custom event for WorkspaceContext
    window.dispatchEvent(new Event('token-set'));
    
    return userData;
  };

  /**
   * Connexion via Google OAuth
   * Échange le session_id temporaire contre un token de session
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const loginWithGoogle = useCallback(async (sessionId) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const response = await fetch(`${API_URL}/api/auth/google/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erreur de connexion Google');
    }

    const data = await response.json();
    const { access_token, user_id, email, name, picture } = data;
    
    // Construire l'objet utilisateur
    const userData = {
      id: user_id,
      email: email,
      prenom: name?.split(' ')[0] || '',
      nom: name?.split(' ').slice(1).join(' ') || '',
      picture: picture,
      role: 'user',
    };
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    // Dispatch custom event for WorkspaceContext
    window.dispatchEvent(new Event('token-set'));
    
    return userData;
  }, []);

  /**
   * Démarrer le flux de connexion Google
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const startGoogleLogin = useCallback(() => {
    // Utiliser window.location.origin pour obtenir l'URL dynamiquement
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    sessionStorage.removeItem('activeSocieteIdOnglet');
    // Appeler le logout backend pour supprimer la session Google si elle existe
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/auth/google/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      // Ignorer les erreurs de logout backend
    }
    
    // Nettoyer TOUT le localStorage pour éviter les conflits de session
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeSocieteId');
    localStorage.removeItem('selectedSocieteId');
    localStorage.removeItem('societeId');
    localStorage.removeItem('workspace');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('theme');
    // Supprimer tout ce qui pourrait rester
    const keysToKeep = []; // On ne garde rien
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    startGoogleLogin,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
